import AbstractSwitch from './AbstractSwitch'

// `trigger` をクリックしたとき `targets` をアクティブにする
/**
 * クリックでターゲット要素を切り替えるスイッチクラス
 *
 * @class ClickSwitch
 * @extends AbstractSwitch
 * @param {Object} options - コンストラクタに渡すオプション
 * @param {HTMLElement} options.trigger - トリガーとなる要素
 * @param {HTMLElement[]} options.targets - 切り替える対象の要素
 * @param {Function} [options.onActive] - アクティブ時に実行されるコールバック
 * @param {Function} [options.onDefault] - デフォルト時に実行されるコールバック
 * @param {boolean} [options.setAriaLabels=true] - aria-labelを設定するかどうか
 * @param {string} [options.activeClass='is-active'] - アクティブ時のクラス名
 * @param {string} [options.activeLabel='閉じる'] - アクティブ時のaria-labelの値
 * @param {string} [options.defaultLabel='開く'] - デフォルト時のaria-labelの値
 */
export default class ClickSwitch extends AbstractSwitch {
  constructor(options) {
    // オプションからユーザー定義のコールバックを取得
    const userOnActive = options.onActive;
    const userOnDefault = options.onDefault;

    super({
      trigger: options.trigger,
      targets: options.targets,
      // 親クラスのonActiveコールバックを拡張
      onActive: (trigger, targets) => {
        // aria-label設定
        if (options.setAriaLabels !== false) {
          trigger.setAttribute('aria-label', this.activeLabel);
        }

        // ユーザー定義のコールバックを実行
        if (typeof userOnActive === 'function') {
          userOnActive(trigger, targets);
        }
      },
      // 親クラスのonDefaultコールバックを拡張
      onDefault: (trigger, targets) => {
        // aria-label設定
        if (options.setAriaLabels !== false) {
          trigger.setAttribute('aria-label', this.defaultLabel);
        }

        // ユーザー定義のコールバックを実行
        if (typeof userOnDefault === 'function') {
          userOnDefault(trigger, targets);
        }
      }
    });

    // デフォルト値の設定
    this.activeClass = options.activeClass || 'is-active';
    this.activeLabel = options.activeLabel || '閉じる';
    this.defaultLabel = options.defaultLabel || '開く';
    this.setAriaLabels = options.setAriaLabels !== false; // デフォルトはtrue
  }

  /**
   * アクティブ状態に遷移する
   */
  toActive() {
    super.toActive();

    // クラスを更新
    this.config.trigger.classList.add(this.activeClass);
    this.config.targets.map(e => {
      e.classList.add(this.activeClass);

      // 子要素の <a> に tabindex を追加
      e.querySelectorAll('a').forEach(link => {
        link.setAttribute('tabindex', '0');
      });
    });

    // 属性を更新
    this.config.trigger.setAttribute('aria-expanded', true);
    // aria-label は onActive コールバックで処理されるため、ここでは設定しない
    this.config.targets.map(e => e.setAttribute('aria-hidden', false));
  }

  /**
   * デフォルト状態に遷移する
   */
  toDefault() {
    super.toDefault();

    // クラスを削除
    this.config.trigger.classList.remove(this.activeClass);
    this.config.targets.map(e => {
      e.classList.remove(this.activeClass);

      // 子要素の <a> に tabindex を追加
      e.querySelectorAll('a').forEach(link => {
        link.setAttribute('tabindex', '-1');
      });
    });

    // 属性を更新
    this.config.trigger.setAttribute('aria-expanded', false);
    // aria-label は onDefault コールバックで処理されるため、ここでは設定しない
    this.config.targets.map(e => e.setAttribute('aria-hidden', true));
  }
}
